/**
 * Текст сообщения с разметкой Telegram.
 *
 * Раньше текст рисовался голым: жирный, ссылки и премиальные эмодзи терялись.
 * Премиальный эмодзи в исходном тексте выглядит обычным символом, поверх
 * которого лежит сущность с идентификатором картинки, — без разбора сущностей
 * пользователь видел не тот эмодзи, который отправил собеседник.
 *
 * Вложенную разметку мы не поддерживаем: пересекающиеся сущности схлопываются
 * во внешнюю. Telegram допускает жирный внутри ссылки, но полноценное дерево
 * ради этого случая усложнило бы отрисовку заметно сильнее, чем стоит выигрыш.
 */

import { Fragment, useState } from 'react';
import type { TelegramTextEntityDto } from '@tw/shared-types';
import { cn } from '@/lib/utils';
import { openInAppBrowser } from '@/lib/externalLink';
import { parseLink } from '@/lib/links';

interface RichTextProps {
  text: string;
  entities: readonly TelegramTextEntityDto[];
  /**
   * Нужен, чтобы запросить изображение премиального эмодзи через чат.
   * Без него картинка берётся общим маршрутом — для заготовок вне переписки.
   */
  chatId?: string;
  /**
   * Открыть чат по @username, не выходя из приложения.
   *
   * Без обработчика ссылки Telegram остаются просто текстом: увести человека
   * во внешний браузер — худший вариант, там он окажется под личным
   * аккаунтом, мимо наших прав и журнала.
   */
  onOpenPeer?: (username: string) => void;
}

export function RichText({ text, entities, chatId, onOpenPeer }: RichTextProps) {
  /*
   * Поле читается защищённо, хотя по типу оно обязательное.
   *
   * Клиент и сервер живут раздельно и обновляются не одновременно: пока
   * работает предыдущая версия API, новых полей в ответе нет. Обращение к
   * длине несуществующего массива роняло ВСЮ ленту, а не одно сообщение —
   * пользователь видел пустой экран вместо переписки.
   *
   * Тип здесь не помогает: он описывает договор, а не то, что реально пришло
   * по сети.
   */
  const list = entities ?? [];
  if (list.length === 0) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (const [index, entity] of list.entries()) {
    // Пересечение с уже отрисованным пропускаем: вложенность не поддержана.
    if (entity.offset < cursor) continue;

    if (entity.offset > cursor) {
      parts.push(<Fragment key={`t${index}`}>{text.slice(cursor, entity.offset)}</Fragment>);
    }

    const slice = text.slice(entity.offset, entity.offset + entity.length);
    parts.push(
      <Entity
        key={`e${index}`}
        entity={entity}
        text={slice}
        chatId={chatId}
        onOpenPeer={onOpenPeer}
      />,
    );
    cursor = entity.offset + entity.length;
  }

  if (cursor < text.length) {
    parts.push(<Fragment key="tail">{text.slice(cursor)}</Fragment>);
  }

  return <>{parts}</>;
}

function Entity({
  entity,
  text,
  chatId,
  onOpenPeer,
}: {
  entity: TelegramTextEntityDto;
  text: string;
  chatId?: string;
  onOpenPeer?: (username: string) => void;
}) {
  const [spoiled, setSpoiled] = useState(false);

  switch (entity.type) {
    case 'customEmoji':
      return <CustomEmoji id={entity.customEmojiId} fallback={text} chatId={chatId} />;

    case 'bold':
      return <strong className="font-semibold">{text}</strong>;

    case 'italic':
      return <em>{text}</em>;

    case 'underline':
      return <span className="underline">{text}</span>;

    case 'strikethrough':
      return <span className="line-through">{text}</span>;

    case 'code':
    case 'pre':
      return (
        <code className="rounded bg-black/20 px-1 font-mono text-[0.9em] dark:bg-white/10">
          {text}
        </code>
      );

    case 'spoiler':
      // Скрытый текст открывается по нажатию — как в Telegram. Показать его
      // сразу значило бы раскрыть то, что отправитель намеренно спрятал.
      return (
        <span
          onClick={() => setSpoiled(true)}
          className={cn(
            'cursor-pointer rounded transition-all',
            !spoiled && 'bg-current text-transparent select-none',
          )}
        >
          {text}
        </span>
      );

    case 'mention':
    case 'url':
    case 'textUrl': {
      const target = parseLink(entity.url ?? text);

      /*
       * Внутренняя ссылка ведёт внутрь.
       *
       * href оставляем настоящим ради контекстного меню и подсказки браузера
       * со ссылкой, но переход перехватываем: иначе Electron открыл бы её
       * вкладкой веб-Telegram, где сидит личный аккаунт менеджера.
       */
      return (
        <a
          href={entity.url ?? text}
          rel="noopener noreferrer"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            if (target.kind === 'peer') {
              onOpenPeer?.(target.username);
              return;
            }

            // Незнакомую ссылку Telegram наружу не выпускаем — см. links.ts.
            if (target.kind === 'external') openInAppBrowser(target.url);
          }}
          className="cursor-pointer underline underline-offset-2 opacity-90 hover:opacity-100"
        >
          {text}
        </a>
      );
    }

    default:
      return <>{text}</>;
  }
}

/**
 * Премиальный эмодзи.
 *
 * При сбое загрузки остаётся исходный символ. Он не всегда похож на
 * премиальный, но это честнее пустого места: смысл фразы часто держится на
 * нём.
 */
function CustomEmoji({
  id,
  fallback,
  chatId,
}: {
  id: string | undefined;
  fallback: string;
  chatId?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (!id || failed) return <>{fallback}</>;

  const src = chatId
    ? `/api/chats/${chatId}/custom-emoji/${encodeURIComponent(id)}`
    : `/api/custom-emoji/${encodeURIComponent(id)}`;

  return (
    <img
      src={src}
      alt={fallback}
      // Идентификатор — в атрибуте: при копировании выделенного текста из
      // ленты он уходит в HTML буфера, и поле ввода восстанавливает эмодзи.
      data-emoji-id={id}
      loading="lazy"
      onError={() => setFailed(true)}
      className="inline-block size-[1.25em] align-[-0.25em]"
    />
  );
}
